import React, { useState, useRef, useEffect } from "react";
import { useMoney } from "../../hooks/useMoney";
import { Calendar, CheckCircle, DollarSign, Wallet, Plus, MoreVertical, FileText, CalendarX, Bell } from "lucide-react";
import type { DistributorProgram } from "../../features/distributor/DistributorSlice";

/* --------------------------------- Helper ------------------------------ */

const formatContractPeriod = (startDate: string): string => {
  const formatDate = (dateString: string, useFullMonth = false): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", {
      month: useFullMonth ? "long" : "short",
    });
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month}, ${year}`;
  };
  return startDate ? `${formatDate(startDate)}` : "-";
};

/* --------------------------------- Component ------------------------------ */

interface AvailableWalletCardProps {
  program: DistributorProgram;
  onYearEndClick: (programId: number) => void;
  onViewTransactions: (programId: number, programName: string) => void;
  onPreviousFySummary: (walletId: number, programName: string, programId: number) => void;
  onAlertSettings?: () => void;
}

const AvailableWalletCard: React.FC<AvailableWalletCardProps> = ({
  program,
  onYearEndClick,
  onViewTransactions,
  onPreviousFySummary,
  onAlertSettings,
}) => {
  const { symbol } = useMoney();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handlePreviousFySummary = () => {
    setMenuOpen(false);
    onPreviousFySummary(
      program.ProgramDistributorWallet.wallet_id,
      program.programName,
      program.programId
    );
  };

  const handleCloseFy = () => {
    setMenuOpen(false);
    onYearEndClick(program.programId);
  };

  const handleAlertSettings = () => {
    setMenuOpen(false);
    onAlertSettings?.();
  };

  const isDisabled = program.ProgramDistributorWallet.isActive === false;

  return (
    <div className={`relative border rounded-lg p-4 bg-white flex flex-col ${isDisabled ? "opacity-60" : ""}`}>
      {/* Header: Logo, Name, Contract Period */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <img
            src={program.logo}
            alt={program.programName}
            className="w-16 h-10 object-contain rounded-lg p-1"
          />
          <h3 className="text-lg font-semibold text-gray-900">
            {program.programName}
          </h3>
          {isDisabled && (
            <span className="px-2 py-0.5 text-xs font-medium bg-red-50 text-red-600 rounded-full border border-red-200">
              Disabled
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600 font-sm">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>
              {formatContractPeriod(
                program.ProgramDistributorWallet.contractStartDate
              )}
            </span>
            <span className=" border-l px-2 border-gray-200">
              {formatContractPeriod(
                program.ProgramDistributorWallet.contractEndDate
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onViewTransactions(program.programId, program.programName)}
            className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white focus:outline-none transition-colors flex items-center gap-2"
          >
            View Transactions
          </button>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
              aria-label="More options"
              aria-expanded={menuOpen}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {onAlertSettings && (
                  <button
                    type="button"
                    onClick={handleAlertSettings}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Bell className="w-4 h-4 text-gray-500" />
                    Alert settings
                  </button>
                )}
                <button
                  type="button"
                  onClick={handlePreviousFySummary}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <FileText className="w-4 h-4 text-gray-500" />
                  Previous FY Summary
                </button>
                <button
                  type="button"
                  onClick={handleCloseFy}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <CalendarX className="w-4 h-4 text-gray-500" />
                  Close FY
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Main Content: Wallet Details & Discount Ranges */}
      <div className="flex flex-row gap-6">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Wallet Details
          </h4>
          <div className="flex flex-col gap-2">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="p-2 mr-5 rounded-full bg-white">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  Current Balance
                </p>
                <p className="text-md mt-1 font-semibold text-gray-900">
                  {symbol}{program.ProgramDistributorWallet.cumulativeBalance}
                </p>
              </div>
            </div>
            <div className="flex items-center  p-3 bg-gray-50 rounded-lg  w-full">
              <div className="p-2 mr-5 rounded-full bg-white">
                <Wallet className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Face Value</p>
                <p className="text-md mt-1 font-semibold text-gray-900">
                  {symbol}{program.ProgramDistributorWallet.cumulativeFaceValue}
                </p>
              </div>
            </div>
            <div className="flex items-center  p-3 bg-gray-50 rounded-lg">
              <div className="p-2 mr-5 rounded-full bg-white">
                <Plus className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Total Top Up</p>
                <p className="text-md mt-1 font-semibold text-gray-900">
                  {symbol}{program.ProgramDistributorWallet.cumulativeTopUp}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Discount Ranges
          </h4>
          {program.ProgramDistributorWallet.discountRanges &&
          program.ProgramDistributorWallet.discountRanges.length > 0 ? (
            <div className="space-y-2">
              {program.ProgramDistributorWallet.discountRanges.map(
                (range, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm text-gray-700">
                        {symbol}{range.min_order_value} to{" "}
                        {range.max_order_value === null
                          ? "∞"
                          : `${symbol}${range.max_order_value}`}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-brand">
                      {range.discount}% discount
                    </span>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">
              No discount ranges available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailableWalletCard;
